import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@/shared/components/Button/Button'
import IconButton from '@/shared/components/IconButton/IconButton'
import Select from '@/shared/components/Select/Select'
import TextField from '@/shared/components/TextField/TextField'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { useFloorPlanStore } from '../../store/useFloorPlanStore'
import { useFloorPlanMutations } from '../../hooks/useFloorPlanMutations'
import RoomManageModal from '../RoomManageModal/RoomManageModal'
import SavePlanButton from '../SavePlanButton/SavePlanButton'
import {
  FLOOR_PLAN_SCHEMA_VERSION,
  FloorTable,
  Room,
  TableShape
} from '../../types/floorPlan.model'
import styles from './FloorPlanToolbar.module.scss'

const MIN_SEATS = 1
const MAX_SEATS = 16
const DEFAULT_SEATS = 4

function clampSeats(value: number): number {
  if (Number.isNaN(value)) return MIN_SEATS
  return Math.min(MAX_SEATS, Math.max(MIN_SEATS, value))
}

interface FloorPlanToolbarProps {
  selectedTableIds: string[]
  onClear: () => void
}

function FloorPlanToolbar(props: FloorPlanToolbarProps) {
  const { selectedTableIds, onClear } = props
  const { t } = useTranslation()

  const shapeOptions: OptionType[] = useMemo(
    () => [
      { value: 'round', label: t('FLOOR_PLAN.SHAPE.ROUND') },
      { value: 'square', label: t('FLOOR_PLAN.SHAPE.SQUARE') },
      { value: 'rectangle', label: t('FLOOR_PLAN.SHAPE.RECTANGLE') }
    ],
    [t]
  )

  const [newShape, setNewShape] = useState<OptionType>(shapeOptions[0])
  const [newSeats, setNewSeats] = useState<string>(String(DEFAULT_SEATS))

  // Room management (add / rename / delete) now lives inside the panel.
  const [roomModalMode, setRoomModalMode] = useState<
    'create' | 'rename' | null
  >(null)
  const rooms = useFloorPlanStore(s => s.rooms)
  const activeRoomId = useFloorPlanStore(s => s.activeRoomId)
  const activeRoom = rooms.find(room => room.id === activeRoomId) ?? null

  // Single-table editing only applies when exactly one table is selected.
  const singleSelectedId =
    selectedTableIds.length === 1 ? selectedTableIds[0] : null

  const selectedTable = useFloorPlanStore(s =>
    singleSelectedId
      ? (s.tables.find(table => table.id === singleSelectedId) ?? null)
      : null
  )

  const snapEnabled = useFloorPlanStore(s => s.snapEnabled)
  const toggleSnap = useFloorPlanStore(s => s.toggleSnap)
  const tool = useFloorPlanStore(s => s.tool)
  const setTool = useFloorPlanStore(s => s.setTool)
  const undo = useFloorPlanStore(s => s.undo)
  const redo = useFloorPlanStore(s => s.redo)
  const canUndo = useFloorPlanStore(s => s.past.length > 0)
  const canRedo = useFloorPlanStore(s => s.future.length > 0)
  const importPlan = useFloorPlanStore(s => s.importPlan)
  const { showSnackbar } = useSnackbar()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Save = download the current plan as a JSON file the user can keep / re-import.
  const handleSavePlan = () => {
    const { rooms, tables } = useFloorPlanStore.getState()
    const payload = { version: FLOOR_PLAN_SCHEMA_VERSION, rooms, tables }
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json'
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'plan-sali.json'
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleLoadPlanClick = () => fileInputRef.current?.click()

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-importing the same file
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as {
          rooms?: Room[]
          tables?: FloorTable[]
        }
        if (!Array.isArray(parsed.rooms) || !Array.isArray(parsed.tables)) {
          throw new Error('Invalid plan file')
        }
        importPlan(parsed.rooms, parsed.tables)
        onClear()
        showSnackbar(
          t('FLOOR_PLAN.ALERTS.IMPORT_SUCCESS.TITLE'),
          t('FLOOR_PLAN.ALERTS.IMPORT_SUCCESS.DESCRIPTION'),
          'success'
        )
      } catch {
        showSnackbar(
          t('FLOOR_PLAN.ALERTS.IMPORT_ERROR.TITLE'),
          t('FLOOR_PLAN.ALERTS.IMPORT_ERROR.DESCRIPTION'),
          'error'
        )
      }
    }
    reader.readAsText(file)
  }

  // Ctrl/Cmd+Z to undo, Ctrl/Cmd+Shift+Z to redo (ignored while typing).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return
      if (event.key.toLowerCase() !== 'z') return
      const target = event.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) {
        return
      }
      event.preventDefault()
      if (event.shiftKey) redo()
      else undo()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [undo, redo])

  const {
    addTable,
    setTableSeatCount,
    setTableShape,
    setTableLabel,
    deleteTable,
    addRoom,
    renameRoom,
    deleteRoom
  } = useFloorPlanMutations()

  const handleRoomConfirm = (name: string) => {
    if (roomModalMode === 'create') addRoom(name)
    else if (roomModalMode === 'rename' && activeRoom)
      renameRoom(activeRoom.id, name)
  }

  const handleRoomDelete = () => {
    if (activeRoom) deleteRoom(activeRoom.id)
  }

  const handleAddTable = () => {
    const seats = clampSeats(parseInt(newSeats, 10))
    addTable((newShape.value as TableShape) ?? 'round', seats)
  }

  const handleNewSeatsChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setNewSeats(e.target.value)

  const handleSelectedSeatsChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!selectedTable) return
    const seats = clampSeats(parseInt(e.target.value, 10))
    setTableSeatCount(selectedTable.id, seats)
  }

  const handleSelectedShapeChange = (value: OptionType | null) => {
    if (!selectedTable || !value) return
    setTableShape(selectedTable.id, value.value as TableShape)
  }

  const handleSelectedLabelChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!selectedTable) return
    setTableLabel(selectedTable.id, e.target.value)
  }

  const handleDelete = () => {
    if (!selectedTable) return
    deleteTable(selectedTable.id)
    onClear()
  }

  const handleDeleteSelected = () => {
    selectedTableIds.forEach(id => deleteTable(id))
    onClear()
  }

  const selectedShapeOption =
    shapeOptions.find(option => option.value === selectedTable?.shape) ?? null

  // Hidden file input is always mounted so Load works in either panel state.
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="application/json,.json"
      onChange={handleFileChange}
      style={{ display: 'none' }}
    />
  )

  const roomModal = (
    <RoomManageModal
      isOpen={roomModalMode !== null}
      mode={roomModalMode ?? 'create'}
      initialName={roomModalMode === 'rename' ? (activeRoom?.name ?? '') : ''}
      onClose={() => setRoomModalMode(null)}
      onConfirm={handleRoomConfirm}
    />
  )

  return (
    <div className={styles.groups}>
      <section className={styles.section}>
        <span className={styles.sectionTitle}>
          {t('FLOOR_PLAN.TOOLBAR.GROUP_ROOMS')}
        </span>
        <div className={styles.buttonRow}>
          <Button
            label={t('FLOOR_PLAN.ROOM.ADD')}
            variant="contained"
            color="primary"
            icon="las la-plus"
            textAlignment="left"
            maxWidth
            onClick={() => setRoomModalMode('create')}
          />
          <Button
            label={t('FLOOR_PLAN.ROOM.RENAME')}
            variant="outlined"
            color="primary"
            icon="las la-edit"
            textAlignment="left"
            maxWidth
            disabled={!activeRoom}
            onClick={() => setRoomModalMode('rename')}
          />
          <Button
            label={t('FLOOR_PLAN.ROOM.DELETE')}
            variant="outlined"
            color="primary"
            icon="las la-trash"
            textAlignment="left"
            maxWidth
            disabled={rooms.length <= 1}
            onClick={handleRoomDelete}
          />
        </div>
      </section>

      <section className={styles.section} id="tour-floor-plan-plan-group">
        <span className={styles.sectionTitle}>
          {t('FLOOR_PLAN.TOOLBAR.GROUP_PLAN')}
        </span>
        <div className={styles.buttonRow}>
          {/* Persist the whole plan to the backend on demand. */}
          <SavePlanButton maxWidth />
          <Button
            label={t('FLOOR_PLAN.TOOLBAR.SNAP_TO_GRID')}
            variant={snapEnabled ? 'contained' : 'outlined'}
            color={snapEnabled ? 'secondary' : 'primary'}
            icon="las la-border-all"
            textAlignment="left"
            maxWidth
            onClick={toggleSnap}
          />
          {/* Local file utilities, export the plan to JSON / import it back. */}
          <Button
            label={t('FLOOR_PLAN.TOOLBAR.EXPORT_PLAN')}
            variant="outlined"
            color="primary"
            icon="las la-file-download"
            textAlignment="left"
            maxWidth
            onClick={handleSavePlan}
          />
          <Button
            label={t('FLOOR_PLAN.TOOLBAR.IMPORT_PLAN')}
            variant="outlined"
            color="primary"
            icon="las la-folder-open"
            textAlignment="left"
            maxWidth
            onClick={handleLoadPlanClick}
          />
        </div>
      </section>

      <section className={styles.section} id="tour-floor-plan-tools-group">
        <span className={styles.sectionTitle}>
          {t('FLOOR_PLAN.TOOLBAR.GROUP_TOOLS')}
        </span>
        <div className={styles.iconRow}>
          <IconButton
            icon="las la-mouse-pointer icon-big"
            color={tool === 'select' ? 'secondary' : 'primary'}
            variant="text"
            onClick={() => setTool('select')}
          />
          <IconButton
            icon="las la-hand-paper icon-big"
            color={tool === 'hand' ? 'secondary' : 'primary'}
            variant="text"
            onClick={() => setTool('hand')}
          />
          <IconButton
            icon="las la-undo icon-big"
            color="primary"
            onClick={undo}
            disabled={!canUndo}
          />
          <IconButton
            icon="las la-redo icon-big"
            color="primary"
            onClick={redo}
            disabled={!canRedo}
          />
        </div>
      </section>

      {/* "New table" and "Selection" are mutually exclusive: showing both made
          the identical shape/seats fields look duplicated. With nothing
          selected you configure the next table; selecting one switches the
          panel to editing that table. */}
      {selectedTableIds.length === 0 && (
        <section className={`${styles.section} ${styles.spacedTitle}`}>
          <span className={styles.sectionTitle}>
            {t('FLOOR_PLAN.TOOLBAR.GROUP_NEW_TABLE')}
          </span>
          <div className={styles.row}>
            <div className={styles.field}>
              <Select
                label={t('FLOOR_PLAN.TOOLBAR.SHAPE')}
                options={shapeOptions}
                value={newShape}
                hideClearIcon
                onChange={value => value && setNewShape(value as OptionType)}
              />
            </div>
            <div className={styles.seatsField}>
              <TextField
                label={t('FLOOR_PLAN.TOOLBAR.SEATS')}
                type="number"
                onlyInteger
                value={newSeats}
                onChange={handleNewSeatsChange}
              />
            </div>
          </div>
          {/* Sits with the fields it uses, so it's clear what gets added. */}
          <Button
            label={t('FLOOR_PLAN.TOOLBAR.ADD_TABLE')}
            variant="contained"
            color="primary"
            icon="las la-plus"
            textAlignment="left"
            maxWidth
            onClick={handleAddTable}
          />
          <span className={styles.hint}>
            {t('FLOOR_PLAN.TOOLBAR.SELECT_HINT')}
          </span>
        </section>
      )}

      {selectedTableIds.length > 0 && (
        <section className={`${styles.section} ${styles.spacedTitle}`}>
          <span className={styles.sectionTitle}>
            {t('FLOOR_PLAN.TOOLBAR.GROUP_EDIT')}
          </span>

          {selectedTable && (
            <div className={styles.stack}>
              <div className={styles.row}>
                <div className={styles.seatsField}>
                  <TextField
                    label={t('FLOOR_PLAN.TOOLBAR.TABLE_NUMBER')}
                    type="number"
                    onlyInteger
                    value={selectedTable.label}
                    onChange={handleSelectedLabelChange}
                  />
                </div>
                <div className={styles.seatsField}>
                  <TextField
                    label={t('FLOOR_PLAN.TOOLBAR.SEATS')}
                    type="number"
                    onlyInteger
                    value={String(selectedTable.seatCount)}
                    onChange={handleSelectedSeatsChange}
                  />
                </div>
              </div>
              <div className={styles.field}>
                <Select
                  label={t('FLOOR_PLAN.TOOLBAR.SHAPE')}
                  options={shapeOptions}
                  value={selectedShapeOption}
                  hideClearIcon
                  onChange={value =>
                    handleSelectedShapeChange(value as OptionType | null)
                  }
                />
              </div>
              <Button
                label={t('FLOOR_PLAN.TOOLBAR.DELETE_TABLE')}
                variant="contained"
                color="secondary"
                icon="las la-trash"
                textAlignment="left"
                maxWidth
                onClick={handleDelete}
              />
            </div>
          )}

          {selectedTableIds.length > 1 && (
            <div className={styles.stack}>
              <span className={styles.selectedCount}>
                {t('FLOOR_PLAN.TOOLBAR.SELECTED_COUNT', {
                  count: selectedTableIds.length
                })}
              </span>
              <Button
                label={t('FLOOR_PLAN.TOOLBAR.DELETE_SELECTED')}
                variant="outlined"
                color="primary"
                icon="las la-trash"
                textAlignment="left"
                maxWidth
                onClick={handleDeleteSelected}
              />
            </div>
          )}
        </section>
      )}

      {fileInput}
      {roomModal}
    </div>
  )
}

export default FloorPlanToolbar
